import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "@/src/helpers";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "qk-editor fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    style={{ background: "transparent", pointerEvents: "auto" }}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "qk-editor qk-sheet-surface fixed z-50 gap-4 p-6 transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom: "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-[min(92vw,390px)] border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
        right:
          "inset-y-0 right-0 h-full w-[min(92vw,390px)] border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

const SheetContent = React.forwardRef(({ side = "right", selected, setSelected, className, children, ...props }, ref) => (
  <SheetPortal>
    <div>
      <SheetOverlay />
      <SheetPrimitive.Content
        ref={ref}
        className={cn(
          sheetVariants({ side }),
          "h-full overflow-y-auto",
          className,
        )}
        style={{
          borderLeft: "1px solid var(--qk-ink)",
          color: "var(--qk-ink)",
          background: "var(--qk-paper-3)",
          boxShadow: "-6px 0 0 var(--qk-ink), -18px 0 40px -8px var(--qk-shadow)",
        }}
        {...props}
      >
        {children}
        <SheetPrimitive.Close
          className="qk-icon-button qk-button-round absolute right-4 top-4 disabled:pointer-events-none"
        >
          <X className="h-3.5 w-3.5" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
        {selected !== null && (
          <button
            type="button"
            className="qk-icon-button qk-button-round absolute left-4 top-4"
            onClick={() => setSelected(null)}
            aria-label="Back to settings"
          >
            <i className="pi pi-arrow-left text-xs"></i>
          </button>
        )}
      </SheetPrimitive.Content>
    </div>
  </SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("qk-display mb-6 text-center", className)}
    style={{ fontSize: 26, fontStyle: "italic", color: "var(--qk-ink)", fontWeight: 400 }}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("qk-hand mb-1 text-center", className)}
    style={{ fontSize: 16, color: "var(--qk-ink-2)" }}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

const SheetItem = React.forwardRef(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("cursor-pointer pb-3 text-center", className)}
    style={{ fontFamily: "'Satoshi', system-ui, sans-serif", fontSize: 14, color: "var(--qk-ink)" }}
    {...props}
  />
));
SheetItem.displayName = "SheetItem";

export {
  Sheet,
  // SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetItem,
  SheetDescription,
};
